'use strict';

/* global Promise */

/* Name of the metadata file to use with the payments client side SDK. */
var METADATA_FILE = 'v1.json';

/** Constant indicating the Adyen gateway payments client side SDK vendor */
var VENDOR_ADYEN = 'Adyen';

/** Constant indicating the PayPal gateway payments client side SDK vendor */
var VENDOR_PAYPAL = 'Paypal';

/** Constant indicating the Stripe gateway payments client side SDK vendor */
var VENDOR_STRIPE = 'Stripe';

/**
 * Builds a theme for use with the payments client side SDK on this site.
 * @param {HTMLElement} element - The element at which to mount the SDK component
 * @param {Object} themeOverrides - Object containing overrides to the default theme
 * @returns {Object} - Complete theme object
 */
function buildTheme(element, themeOverrides) {
    // Get style for element at which to mount
    const style = window.getComputedStyle(element);

    /**
     * Returns the computed CSS style for the given property, or the fallback if not set.
     * @param {string} property - CSS property name
     * @param {string} fallback - fallback value if CSS property not set
     * @returns {string} - Property value or fallback
     */
    function cssVar(property, fallback) {
        // Get the computed CSS variable value for the element style
        const value = style.getPropertyValue(property);
        return value || fallback;
    }

    var defaultTheme = {
        designTokens: {
            'font-family': cssVar('--skin-body-font', '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'),
            'line-height-text': '1.5',
            'color-text-default': cssVar('--skin-main-text-color-1', '#212529'),
            'color-text-secondary': cssVar('--color-black', '#000')
        },
        fonts: [],
        rules: {
            button: {
                // Match btn-primary styling (skin.scss)
                'background-color': cssVar('--skin-primary-color-1', '#622A3A'),
                border: '0',
                'border-radius': cssVar('--skin-primary-border-radius', '2.625rem'),
                color: cssVar('--skin-button-primary-text-color', '#FCF2E1'),
                'font-weight': cssVar('--skin-font-weight-medium', '500'),
                'line-height': '1',
                padding: cssVar('--skin-primary-padding-small', '16px 32px'),
                transition: 'opacity 0.15s ease-in-out',
                active: {
                    'background-color': cssVar('--skin-primary-color-1', '#622A3A'),
                    border: '0',
                    color: cssVar('--skin-button-primary-text-color', '#FCF2E1'),
                    'box-shadow': 'none',
                    outline: '0'
                },
                focus: {
                    'background-color': cssVar('--skin-primary-color-1', '#622A3A'),
                    border: '0',
                    color: cssVar('--skin-button-primary-text-color', '#FCF2E1'),
                    'box-shadow': 'none',
                    outline: '0'
                },
                hover: {
                    'background-color': cssVar('--skin-primary-color-1', '#622A3A'),
                    border: '0',
                    color: cssVar('--skin-button-primary-text-color', '#FCF2E1'),
                    opacity: '0.8'
                }
            },
            error: {
                color: '#c00',
                'font-size': '14px',
                margin: '4px 0 16px 0'
            },
            formLabel: {
                color: cssVar('--skin-main-text-color-1', '#212529'),
                'font-size': '16px',
                margin: '0 0 8px 0',
                padding: '0',
                invalid: {
                    color: cssVar('--skin-main-text-color-1', '#212529')
                }
            },
            input: {
                // Match form-control styling (checkout.scss + globalFormControls.scss)
                'background-color': cssVar('--color-white', '#fff'),
                border: '1px solid ' + (cssVar('--color-menu-icon-background-color', '#3917171f')),
                'border-radius': '6px',
                'font-size': '16px',
                height: '50px',
                width: '100%',
                'line-height': '1.2',
                margin: '0 0 15px 0',
                padding: '15px 15px',
                color: cssVar('--color-dark', '#361F1A'),
                focus: {
                    border: '1px solid ' + (cssVar('--color-menu-icon-background-color', '#3917171f')),
                    'box-shadow': 'none',
                    outline: '0',
                    transition:
                        'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out, -webkit-box-shadow 0.15s ease-in-out'
                },
                invalid: {
                    border: '1px solid ' + (cssVar('--color-error-border', '#ce2525')),
                    margin: '0',
                    focus: {
                        border: '1px solid ' + (cssVar('--color-error-border', '#ce2525')),
                        'box-shadow': '0 0 0 0.2rem ' + (cssVar('--color-error-box-shadow', 'rgba(183, 76, 76, 0.25)')),
                        outline: '0'
                    }
                }
            },
            picklist: {
                'background-color': '#fff',
                border: '1px solid rgba(0, 0, 0, 0.15)',
                color: '#212529'
            },
            picklistOption: {
                'background-color': '#fff',
                border: '0',
                color: '#212529',
                padding: '4px 24px',
                hover: {
                    'background-color': '#e9ecef'
                }
            }
        }
    };
    return $.extend(true, {}, defaultTheme, themeOverrides);
}

/**
 * Updates relative image paths to absolute URLs in the given metadata object.
 * @param {Object} metadata - The metadata object
 * @param {string} internalStaticContentUrl - URL to base of internal static content area
 * @returns {Object} - The given metadata object with image URLs resolved
 */
function updateImagePaths(metadata, internalStaticContentUrl) {
    // Process each payment method type and convert relative paths to absolute URLs
    Object.keys(metadata.paymentMethodTypes).forEach(function (methodType) {
        var method = metadata.paymentMethodTypes[methodType];
        method.images.forEach(function (image, index) {
            if (image.src) {
                method.images[index].src = internalStaticContentUrl + image.src;
            }
        });
    });

    return metadata;
}

/**
 * Fetches the payments metadata to use with the payments client side SDK on this site.
 * @param {string} internalStaticContentUrl - String URL to base of internal static content area
 * @returns {Promise<Object>} - Promise to resolve the retrieved metadata object
 */
function fetchMetadata(internalStaticContentUrl) {
    var metadataUrl = internalStaticContentUrl + '/metadata/' + METADATA_FILE;

    return new Promise(function (resolve, reject) {
        $.getJSON(metadataUrl).done(function (metadata) {
            resolve(updateImagePaths(metadata, internalStaticContentUrl));
        }).fail(reject);
    });
}

/**
 * Returns the account in the given payment method set for use to present the given payment method type.
 * @param {Object} paymentMethodSet - Payment method set used with the payments client side SDK
 * @param {string} paymentMethodType - Payment method type for which to find the account
 * @returns {Object} - Account for the payment method, or undefined if not found
 */
function findAccount(paymentMethodSet, paymentMethodType) {
    // Find the payment method in the payment method set with the given type
    var paymentMethod = paymentMethodSet.paymentMethods.find(function (method) {
        return method.paymentMethodType === paymentMethodType;
    });

    // Find the account in the payment method set with the account id, if there is one
    var accountId = paymentMethod ? paymentMethod.accountId : null;
    return paymentMethodSet.paymentMethodSetAccounts.find(function (account) {
        return account.accountId === accountId;
    });
}

/**
 * Navigates the browser to the confirmation page for the given order.
 * @param {string} orderConfirmUrl - Order confirmation page URL
 * @param {string} orderNo - Order no of the order
 * @param {string} orderToken - Order token of the order
 */
function navigateToOrderConfirmation(orderConfirmUrl, orderNo, orderToken) {
    var form = $('<form>')
        .appendTo(document.body)
        .attr({
            method: 'POST',
            action: orderConfirmUrl
        });
    $('<input>')
        .appendTo(form)
        .attr({
            name: 'orderID',
            value: orderNo
        });
    $('<input>')
        .appendTo(form)
        .attr({
            name: 'orderToken',
            value: orderToken
        });

    form.submit();
}

/**
 * Handles the approval of an express payment.
 * @param {string} approveExpressPaymentUrl - Approve express payment URL
 * @param {Object} parameters - Controller call parameters
 * @param {Object} parameters.billingDetails - Billing detail information
 * @param {Object} parameters.shippingDetails - Shipping detail information
 * @param {string} parameters.basketId - Optional id of temporary basket whose information to use for payment
 * @returns {Promise<Object>} - Promise to resolve when the approval has been handled
 */
async function approveExpressPayment(approveExpressPaymentUrl, parameters) {
    var body = {
        billingDetails: parameters.billingDetails,
        shippingDetails: parameters.shippingDetails
    };
    if (parameters.basketId) {
        body.basketId = parameters.basketId;
    }

    var result = await $.ajax({
        url: approveExpressPaymentUrl,
        method: 'POST',
        contentType: 'application/json; charset=UTF-8',
        data: JSON.stringify(body)
    });
    if (result.error) {
        return {
            error: result.error.message
        };
    }

    return {};
}

/**
 * Fails the given order.
 * @param {string} failOrderUrl - Fail order URL
 * @param {Object} parameters - Controller call parameters
 * @param {Object} parameters.order - Order to fail
 * @param {string} parameters.order.orderNo - Order no of the order to fail
 * @param {string} parameters.order.orderToken - Order token of the order to fail
 * @returns {Promise<Object>} - Promise to resolve when the order has been failed
 */
async function failOrder(failOrderUrl, parameters) {
    var body = {
        orderID: parameters.order.orderNo,
        orderToken: parameters.order.orderToken
    };

    var result = await $.ajax({
        url: failOrderUrl,
        method: 'POST',
        contentType: 'application/json; charset=UTF-8',
        data: JSON.stringify(body)
    });
    if (result.error) {
        return {
            error: result.error.message
        };
    }

    return {};
}

/**
 * Gets the payment method set using the given information.
 * @param {string} getPaymentMethodSetUrl - Get payment method set URL
 * @param {Object} parameters - Controller call parameters
 * @param {string} parameters.billingCountry - Optional billing address country code
 * @returns {Promise<Object>} - Promise to resolve the payment method set and related information
 */
function getPaymentMethodSet(getPaymentMethodSetUrl, parameters) {
    var data = {};
    if (parameters && parameters.billingCountry) {
        data.billingCountry = parameters.billingCountry;
    }

    return $.ajax({
        url: getPaymentMethodSetUrl,
        method: 'GET',
        data: data
    });
}

/**
 * Prepares a temporary basket using the given information.
 * @param {string} prepareBasketUrl - Prepare basket URL
 * @param {Object} parameters - Controller call parameters
 * @param {Object} parameters.basketData - Information about the basket to create
 * @returns {Promise<Object>} - Promise to resolve the ID of the created temporary basket
 */
async function prepareBasket(prepareBasketUrl, parameters) {
    var body = $.extend({}, parameters.basketData);

    var result = await $.ajax({
        url: prepareBasketUrl,
        method: 'POST',
        contentType: 'application/json; charset=UTF-8',
        data: JSON.stringify(body)
    });
    if (result.error) {
        return {
            error: result.error.message
        };
    }

    return {
        basketId: result.basketId
    };
}

/**
 * Handles an express checkout shipping address change.
 * @param {string} shippingAddressChangeUrl - Shipping address change URL
 * @param {Object} parameters - Controller call parameters
 * @param {Object} parameters.shippingAddress - Selected shipping address
 * @param {string} parameters.basketId - Optional id of temporary basket whose information to use for payment
 * @returns {Promise<Object>} - Promise to resolve updated express checkout information
 */
async function shippingAddressChange(shippingAddressChangeUrl, parameters) {
    var body = {
        shippingAddress: parameters.shippingAddress
    };
    if (parameters.basketId) {
        body.basketId = parameters.basketId;
    }

    var result = await $.ajax({
        url: shippingAddressChangeUrl,
        method: 'POST',
        contentType: 'application/json; charset=UTF-8',
        data: JSON.stringify(body)
    });
    if (result.error) {
        return {
            error: result.error.message
        };
    }

    return {
        output: {
            total: result.total,
            shippingMethods: result.shippingMethods,
            selectedShippingMethod: result.selectedShippingMethod,
            lineItems: result.lineItems
        }
    };
}

/**
 * Handles an express checkout shipping method change.
 * @param {string} shippingMethodChangeUrl - Shipping method change URL
 * @param {Object} parameters - Controller call parameters
 * @param {Object} parameters.shippingMethod - Selected shipping method
 * @param {string} parameters.basketId - Optional id of temporary basket whose information to use for payment
 * @returns {Promise<Object>} - Promise to resolve updated express checkout information
 */
async function shippingMethodChange(shippingMethodChangeUrl, parameters) {
    var body = {
        shippingMethod: parameters.shippingMethod
    };
    if (parameters.basketId) {
        body.basketId = parameters.basketId;
    }

    var result = await $.ajax({
        url: shippingMethodChangeUrl,
        method: 'POST',
        contentType: 'application/json; charset=UTF-8',
        data: JSON.stringify(body)
    });
    if (result.error) {
        return {
            error: result.error.message
        };
    }

    return {
        output: {
            total: result.total,
            shippingMethods: result.shippingMethods,
            selectedShippingMethod: result.selectedShippingMethod,
            lineItems: result.lineItems
        }
    };
}

/**
 * Handles Adyen additional payment details.
 * @param {string} handleAdyenAdditionalDetailsUrl - Handle Adyen additional payment details URL
 * @param {Object} parameters - Controller call parameters
 * @param {string} parameters.zoneId - Payment zone ID
 * @param {*} parameters.paymentData - Adyen payment data
 * @param {Object} parameters.order - Order whose payment to update
 * @param {string} parameters.order.orderNo - Order no of the order whose payment to update
 * @param {string} parameters.order.orderToken - Order token of the order whose payment to update
 * @returns {Promise<Object>} - Promise to resolve when details are updated
 */
async function adyenHandleAdditionalPaymentDetails(handleAdyenAdditionalDetailsUrl, parameters) {
    var body = {
        zoneId: parameters.zoneId,
        paymentData: parameters.paymentData,
        orderId: parameters.order.orderNo,
        orderToken: parameters.order.orderToken
    };

    var result = await $.ajax({
        url: handleAdyenAdditionalDetailsUrl,
        method: 'POST',
        contentType: 'application/json; charset=UTF-8',
        data: JSON.stringify(body)
    });
    if (result.error) {
        return {
            error: result.serverErrors && result.serverErrors.length > 0 ? result.serverErrors[0] : null
        };
    }

    return {};
}

/**
 * Prepares an Adyen payment, and creates an order, using the given information.
 * @param {string} prepareAdyenPaymentUrl - Prepare Adyen payment URL
 * @param {Object} parameters - Controller call parameters
 * @param {string} parameters.zoneId - Payment zone ID
 * @param {*} parameters.paymentData - Adyen payment data
 * @param {string} parameters.paymentMethodType - Payment method type
 * @param {boolean} parameters.cardCaptureAutomatic - True if automatic capture, false if manual capture
 * @param {boolean} parameters.customerRequired - Optional true if a customer reference needs to be created, false if not, default is false
 * @param {boolean} parameters.storePaymentMethod - Optional true if the payment method is to be stored, false if not, default is false
 * @param {string} parameters.basketId - Optional id of temporary basket whose information to use for payment
 * @returns {Promise<Object>} - Promise to resolve information about the created payment intent and order
 */
async function adyenPreparePayment(prepareAdyenPaymentUrl, parameters) {
    var body = {
        zoneId: parameters.zoneId,
        paymentData: parameters.paymentData,
        paymentMethodType: parameters.paymentMethodType,
        cardCaptureAutomatic: parameters.cardCaptureAutomatic,
        customerRequired: parameters.customerRequired === true,
        storePaymentMethod: parameters.storePaymentMethod === true
    };
    if (parameters.basketId) {
        body.basketId = parameters.basketId;
    }

    var result = await $.ajax({
        url: prepareAdyenPaymentUrl,
        method: 'POST',
        contentType: 'application/json; charset=UTF-8',
        data: JSON.stringify(body)
    });
    if (result.error) {
        return {
            error: result.error.message
        };
    }

    return {
        order: {
            orderNo: result.data.orderNo,
            orderToken: result.data.orderToken
        },
        payment: {
            pspReference: result.data.pspReference,
            action: result.data.action,
            resultCode: result.data.resultCode
        }
    };
}

/**
 * Handles PayPal order approval, and creates an order.
 * @param {string} approvePayPalOrderUrl - Approve PayPal order url
 * @param {Object} parameters - Controller call parameters
 * @param {string} parameters.basketId - Optional id of temporary basket whose information to use for payment
 * @returns {Promise<Object>} - Promise to resolve information about the created order
 */
async function paypalOnApprove(approvePayPalOrderUrl, parameters) {
    var body = {};
    if (parameters && parameters.basketId) {
        body.basketId = parameters.basketId;
    }

    var result = await $.ajax({
        url: approvePayPalOrderUrl,
        method: 'POST',
        contentType: 'application/json; charset=UTF-8',
        data: JSON.stringify(body)
    });
    if (result.error) {
        return {
            error: result.error.message
        };
    }

    return {
        order: {
            orderNo: result.data.orderNo,
            orderToken: result.data.orderToken
        }
    };
}

/**
 * Prepares a PayPal order, and creates a temporary basket, using the given information.
 * @param {string} preparePaypalBasketUrl - Prepare PayPal basket URL
 * @param {Object} parameters - Controller call parameters
 * @param {Object} parameters.basketData - Information about the basket to create
 * @param {string} parameters.zoneId - Payment zone ID
 * @param {string} parameters.shippingPreference - PayPal shipping preference
 * @param {string} parameters.userAction - PayPal user action
 * @param {string} parameters.fundingSource - PayPal funding source, either "paypal" or "venmo"
 * @returns {Promise<Object>} - Promise to resolve information about the created PayPal order and temporary basket
 */
async function paypalPrepareBasket(preparePaypalBasketUrl, parameters) {
    var body = $.extend({}, parameters.basketData, {
        zoneId: parameters.zoneId,
        shippingPreference: parameters.shippingPreference,
        userAction: parameters.userAction,
        fundingSource: parameters.fundingSource
    });

    var result = await $.ajax({
        url: preparePaypalBasketUrl,
        method: 'POST',
        contentType: 'application/json; charset=UTF-8',
        data: JSON.stringify(body)
    });
    if (result.error) {
        return {
            error: result.error.message
        };
    }

    return {
        basketId: result.data.basketId,
        paypalOrder: {
            id: result.data.id
        }
    };
}

/**
 * Prepares a PayPal order using the given information.
 * @param {string} preparePaypalOrderUrl  - Prepare PayPal order URL
 * @param {Object} parameters - Controller call parameters
 * @param {string} parameters.zoneId - Payment zone ID
 * @param {string} parameters.shippingPreference - PayPal shipping preference
 * @param {string} parameters.userAction - PayPal user action
 * @param {string} parameters.fundingSource - PayPal funding source, either "paypal" or "venmo"
 * @returns {Promise<Object>} - Promise to resolve information about the created PayPal order
 */
async function paypalPrepareOrder(preparePaypalOrderUrl, parameters) {
    var body = {
        zoneId: parameters.zoneId,
        shippingPreference: parameters.shippingPreference,
        userAction: parameters.userAction,
        fundingSource: parameters.fundingSource,
        subscribeToNewsletter: parameters.subscribeToNewsletter || false
    };
    var result = await $.ajax({
        url: preparePaypalOrderUrl,
        method: 'POST',
        contentType: 'application/json; charset=UTF-8',
        data: JSON.stringify(body)
    });
    if (result.error) {
        return {
            error: result.error.message
        };
    }

    return {
        paypalOrder: {
            id: result.data.id
        }
    };
}

/**
 * Prepares a Stripe payment intent, and creates an order, using the given information.
 * @param {string} prepareStripePaymentIntentUrl - Prepare Stripe payment intent URL
 * @param {Object} parameters - Controller call parameters
 * @param {string} parameters.zoneId - Payment zone ID
 * @param {string} parameters.paymentMethodType - Payment method type
 * @param {boolean} parameters.cardCaptureAutomatic - True if automatic capture, false if manual capture
 * @param {boolean} parameters.customerRequired - Optional true if a customer reference needs to be created, false if not, default is false
 * @param {boolean} parameters.storePaymentMethod - Optional true if the payment method is to be stored, false if not, default is false
 * @param {boolean} parameters.setShippingData - Optional true if shipping information should be set on the payment intent, false if not, default is false
 * @param {string} parameters.basketId - Optional id of temporary basket whose information to use for payment
 * @returns {Promise<Object>} - Promise to resolve information about the created payment intent and order
 */
async function stripePreparePaymentIntent(prepareStripePaymentIntentUrl, parameters) {
    var body = {
        zoneId: parameters.zoneId,
        paymentMethodType: parameters.paymentMethodType,
        cardCaptureAutomatic: parameters.cardCaptureAutomatic,
        customerRequired: parameters.customerRequired === true,
        storePaymentMethod: parameters.storePaymentMethod === true,
        setShippingData: parameters.setShippingData === true
    };
    if (parameters.basketId) {
        body.basketId = parameters.basketId;
    }
    if (parameters.paymentMethodType === 'applepay' || parameters.paymentMethodType === 'googlepay') {
        // Substitute card payment method for Apple Pay and Google Pay
        body.paymentMethodType = 'card';
    }

    var result = await $.ajax({
        url: prepareStripePaymentIntentUrl,
        method: 'POST',
        contentType: 'application/json; charset=UTF-8',
        data: JSON.stringify(body)
    });
    if (result.error) {
        return {
            error: result.error.message
        };
    }

    if (!result.data) {
        return {
            error: result.error ? result.error.message : ''
        };
    }

    return {
        order: {
            orderNo: result.data.orderNo,
            orderToken: result.data.orderToken
        },
        paymentIntent: {
            setup_future_usage: result.data.setup_future_usage,
            client_secret: result.data.client_secret,
            shipping: result.data.shipping
        }
    };
}

module.exports = {
    VENDOR_ADYEN: VENDOR_ADYEN,
    VENDOR_PAYPAL: VENDOR_PAYPAL,
    VENDOR_STRIPE: VENDOR_STRIPE,
    approveExpressPayment: approveExpressPayment,
    buildTheme: buildTheme,
    failOrder: failOrder,
    fetchMetadata: fetchMetadata,
    findAccount: findAccount,
    getPaymentMethodSet: getPaymentMethodSet,
    navigateToOrderConfirmation: navigateToOrderConfirmation,
    prepareBasket: prepareBasket,
    shippingAddressChange: shippingAddressChange,
    shippingMethodChange: shippingMethodChange,
    adyen: {
        handleAdditionalPaymentDetails: adyenHandleAdditionalPaymentDetails,
        preparePayment: adyenPreparePayment
    },
    paypal: {
        onApprove: paypalOnApprove,
        prepareBasket: paypalPrepareBasket,
        prepareOrder: paypalPrepareOrder
    },
    stripe: {
        preparePaymentIntent: stripePreparePaymentIntent
    }
};
